import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  LoadingDialogComponent,
} from "@cotecna/dialogs";
import { firstValueFrom } from "rxjs";
import { AccountSettingsAvailableTags } from "src/app/settings/models/available-form";

import { PhotoReportOrientationType } from "../../model/photo-report-orientation";
import { PhotoReportTagPositionType } from "../../model/photo-report-tagPositionType";
import { InspectionDetailsService } from "../../services/inspection-details.service";

export interface ReportSection {
  id: number;
  name: string;
  tags: AccountSettingsAvailableTags[];
  collapsed: boolean;
  isEditing: boolean;
}

export interface PicturesReportConfigDialogData {
  availableTags: AccountSettingsAvailableTags[];
  templateId?: string | null;
  inspectionId: string;
}

export interface PicturesReportConfigDialogResult {
  reportName: string;
  photosPerRow: number;
  photoLabels: PhotoReportTagPositionType;
  sections: { name: string; tagIds: string[] }[];
}

export const PHOTO_TAGS_TYPES = [
  {
    value: PhotoReportTagPositionType.DontPrint,
    label: "Don't Print Labels/Tags",
  },
  {
    value: PhotoReportTagPositionType.PrintAbovePictures,
    label: "Print Labels/Tags Above Pictures",
  },
  {
    value: PhotoReportTagPositionType.PrintBelowPictures,
    label: "Print Labels/Tags Below Pictures",
  },
];

export const PHOTO_ORIENTATION_TYPES = [
  { value: PhotoReportOrientationType.Portrait, label: "Portrait" },
  { value: PhotoReportOrientationType.Landscape, label: "Landscape" },
];

@Component({
  selector: "im-pictures-report-config-dialog",
  templateUrl: "./pictures-report-config-dialog.component.html",
  styleUrls: ["./pictures-report-config-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PicturesReportConfigDialogComponent implements OnInit {
  public reportName: string = "";
  public photosPerRow: number = 3;
  public numberedPictures: boolean = false;
  public templateSaved: boolean = false;
  public templateId: string | null = null;
  public inspectionId: string = "";

  public get isNumberedPicturesDisabled(): boolean {
    return this.photoTagOption === PhotoReportTagPositionType.DontPrint;
  }

  public onPhotoTagOptionChange(): void {
    if (this.photoTagOption === PhotoReportTagPositionType.DontPrint) {
      this.numberedPictures = false;
    }
    this.markUnsaved();
  }

  public onPhotoOrientationOptionChange(selectedOrientation: number): void {
    this.selectedOrientation =
      selectedOrientation === 1
        ? PhotoReportOrientationType.Portrait
        : PhotoReportOrientationType.Landscape;
    this.markUnsaved();
  }

  public clampPhotosPerRow(): void {
    this.photosPerRow = Math.min(3, Math.max(1, this.photosPerRow || 1));
  }
  public photoTagOption: PhotoReportTagPositionType =
    PhotoReportTagPositionType.DontPrint;
  public photoTagsOptions = PHOTO_TAGS_TYPES;
  public sections: ReportSection[] = [];
  public availableTags: AccountSettingsAvailableTags[] = [];
  public photoOrientationOptions = PHOTO_ORIENTATION_TYPES;
  public selectedOrientation = PhotoReportOrientationType.Portrait;
  private sectionCounter = 0;

  public constructor(
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly dialogRef: MatDialogRef<PicturesReportConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: PicturesReportConfigDialogData,
    private readonly inspectionDetailsService: InspectionDetailsService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.availableTags = this.dialogData?.availableTags ?? [];
    this.inspectionId = this.dialogData?.inspectionId ?? null;
    this.templateId = this.dialogData?.templateId ?? null;
    if (this.templateId) {
      const template =
        await this.inspectionDetailsService.getPhotoReportTemplate(
          this.templateId,
        );
      this.reportName = template.name;
      this.photosPerRow = template.photosPerRow;
      this.numberedPictures = template.numberedPictures;
      this.photoTagOption = template.tagPositionType;
      this.selectedOrientation = template.pageOrientation;
      this.sections = template.sections.map((s) => ({
        id: ++this.sectionCounter,
        name: s.name,
        tags: s.tagsId
          .map((tagId) =>
            this.dialogData.availableTags.find((t) => t.id === tagId),
          )
          .filter((t): t is AccountSettingsAvailableTags => !!t),
        collapsed: false,
        isEditing: false,
      }));
      this.templateSaved = true;
    } else {
      this.addSection();
    }
  }

  public get canGenerate(): boolean {
    return this.reportName.trim().length > 0 && this.templateSaved;
  }

  public get canSaveTemplate(): boolean {
    const names = this.sections.map((s) => s.name.trim().toLowerCase());
    const hasduplicateNames = names.length !== new Set(names).size;
    return (
      !this.templateSaved &&
      this.reportName.trim().length > 0 &&
      this.sections.length > 0 &&
      this.sections.every(
        (s) => s.tags.length > 0 && s.name.trim().length > 0,
      ) &&
      !hasduplicateNames
    );
  }

  public addSection(): void {
    this.sectionCounter++;
    this.sections.push({
      id: this.sectionCounter,
      name: `Section ${this.sectionCounter}`,
      tags: [],
      collapsed: false,
      isEditing: false,
    });
    this.markUnsaved();
  }

  public editSection(section: ReportSection): void {
    section.isEditing = true;
  }

  public stopEditing(section: ReportSection): void {
    section.isEditing = false;
  }

  public duplicateSection(section: ReportSection): void {
    this.sectionCounter++;
    const baseName = `${section.name} (copy)`;
    const existingNames = new Set(
      this.sections.map((s) => s.name.trim().toLowerCase()),
    );
    let copyName = baseName;
    let suffix = 1;

    while (existingNames.has(copyName.trim().toLowerCase())) {
      copyName = `${baseName} ${suffix}`;
      suffix++;
    }

    const copy: ReportSection = {
      id: this.sectionCounter,
      name: copyName,
      tags: [...section.tags],
      collapsed: section.collapsed,
      isEditing: false,
    };
    const index = this.sections.indexOf(section);
    this.sections.splice(index + 1, 0, copy);
    this.markUnsaved();
  }

  public toggleSection(section: ReportSection): void {
    section.collapsed = !section.collapsed;
  }

  public async deleteSection(section: ReportSection): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: <ConfirmDialogData>{
            icon: "delete_outline",
            title: "Delete Section",
            message:
              "Deleting this section will remove all photos and tags within it. Do you want to continue?",
            okButton: { text: "Delete", color: "warn" },
            cancelButton: { text: "Cancel" },
          },
          autoFocus: false,
        })
        .afterClosed(),
    );

    if (!confirmed) {
      return;
    }

    const index = this.sections.findIndex((s) => s.id === section.id);
    if (index < 0) {
      return;
    }

    this.sections.splice(index, 1);
    this.markUnsaved();
  }

  public async cancel(): Promise<void> {
    if (this.canSaveTemplate) {
      const confirmed = await firstValueFrom(
        this.dialog
          .open(ConfirmDialogComponent, {
            data: <ConfirmDialogData>{
              icon: "cancel",
              title: "Close Template",
              message:
                "Changes made to the template are not saved. Are you sure you want to close the template?",
              okButton: { text: "Close", color: "primary" },
              cancelButton: { text: "Cancel" },
            },
            autoFocus: false,
          })
          .afterClosed(),
      );
      if (!confirmed) return;
    }
    this.dialogRef.close(null);
  }

  public async saveTemplate(): Promise<void> {
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: <ConfirmDialogData>{
            icon: "save",
            title: "Save Template",
            message:
              "Saving this template will make it available to everyone in the office.",
            okButton: { text: "Save", color: "accent" },
            cancelButton: { text: "Cancel" },
          },
          autoFocus: false,
        })
        .afterClosed(),
    );
    if (confirmed) {
      const savedId =
        await this.inspectionDetailsService.savePhotoReportTemplate({
          templateId: this.templateId,
          name: this.reportName,
          photosPerRow: this.photosPerRow,
          numberedPictures: this.numberedPictures,
          tagPositionType: this.photoTagOption,
          sections: this.sections.map((s) => ({
            name: s.name,
            tagsId: s.tags.map((t) => t.id),
          })),
          pageOrientation: this.selectedOrientation,
        });

      if (!this.dialogData?.templateId) {
        this.templateId = savedId;
      }

      this.templateSaved = true;
      this.snackBar.open("Template is saved successfully", "✕", {
        duration: 4000,
        panelClass: ["snack-success-green"],
      });
    }
  }

  public markUnsaved(): void {
    this.templateSaved = false;
  }

  public async deleteTemplate(): Promise<void> {
    if (!this.templateId) return;
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: <ConfirmDialogData>{
            icon: "delete_outline",
            title: "Delete Template",
            message: "Are you sure you want to delete this template?",
            okButton: { text: "Delete", color: "warn" },
            cancelButton: { text: "Cancel" },
          },
          autoFocus: false,
        })
        .afterClosed(),
    );
    if (confirmed) {
      await this.inspectionDetailsService.deletePhotoReportTemplate(
        this.templateId,
      );
      this.dialogRef.close(null);
      this.snackBar.open(
        `Template ${this.reportName} has been deleted successfully`,
        "✕",
        {
          duration: 4000,
        },
      );
    }
  }

  public async generateReport(): Promise<void> {
    const loading = this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: "Generating report...",
    });
    try {
      await this.inspectionDetailsService.generatePhotoReport(
        this.templateId ?? "",
        this.inspectionId,
      );
      this.dialogRef.close();
      this.snackBar.open(
        "Pictures Report is being generated and will be available in the Documents tab once ready.",
        "",
        { duration: 6000, panelClass: ["snack-success-green"] },
      );
    } finally {
      loading.close();
    }
  }
}
